import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { cbtAPI } from '../../api/cbt';
import { Sidebar } from './dashboard_component';

const TestResultDetailsPage = () => {
  const { internalTestId } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  useEffect(() => {
    fetchDetails();
  }, [internalTestId]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const response = await cbtAPI.getStudentResultAnalytics(internalTestId);
      setDetails(response.data);
    } catch (error) {
      console.error('Error fetching test details:', error);
      toast.error('Failed to load test details');
      navigate('/participate/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    const secs = Number(seconds);
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return `${mins}m ${remainingSecs}s`;
  };

  const toNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  const getScoreColor = (percent) => {
    if (percent >= 70) return 'text-green-600';
    if (percent >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const toggleQuestionExpand = (index) => {
    setExpandedQuestion(expandedQuestion === index ? null : index);
  };

  // Helper to safely render HTML content
  const renderHtml = (content, fallback = 'Not answered') => {
    if (!content) return fallback;
    return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: content }} />;
  };

  // Get question type icon and label
  const getQuestionTypeInfo = (type) => {
    const types = {
      multiple_choice: { icon: '☑️', label: 'Multiple Choice' },
      single_choice: { icon: '🔘', label: 'Single Choice' },
      true_false: { icon: '✅❌', label: 'True / False' },
      fill_blank: { icon: '_______', label: 'Fill in the Blank' },
      essay: { icon: '📝', label: 'Essay' },
      theory: { icon: '📄', label: 'Theory' },
    };
    return types[type] || { icon: '❓', label: type || 'Question' };
  };

  // Determine if the answer is correct based on score_obtained > 0
  const isAnswerCorrect = (answer) => {
    // Trust score_obtained > 0 as correct (handles inconsistent is_correct flags)
    const score = toNumber(answer.score_obtained);
    if (score > 0) return true;
    // Fallback to is_correct flag if score is 0
    return answer.is_correct === true;
  };

  // Extract correct answer text for a question (from options or dedicated field)
  const getCorrectAnswerText = (answer) => {
    const { question_type, options, correct_answer } = answer;

    if (question_type === 'true_false') {
      // For true/false, if the student got points, we know they were correct,
      // but we don't have the exact correct value to display. Show generic message.
      if (isAnswerCorrect(answer)) {
        return 'Correct answer recorded';
      }
      return 'Not available';
    }

    if (question_type === 'fill_blank') {
      // First try correct_answer field, else use the first option's text (if any)
      if (correct_answer) return correct_answer;
      if (options && options.length > 0 && options[0].text) return options[0].text;
      return 'Not available';
    }

    if (question_type === 'multiple_choice' || question_type === 'single_choice') {
      if (correct_answer) {
        if (Array.isArray(correct_answer)) {
          const texts = correct_answer.map(id => options?.find(opt => opt.id === id)?.text).filter(t => t);
          return texts.join(', ');
        }
        const opt = options?.find(opt => opt.id === correct_answer);
        return opt?.text || correct_answer;
      }
      const correctOpts = options?.filter(opt => opt.is_correct === true);
      return correctOpts?.map(opt => opt.text).join(', ') || 'Not available';
    }

    return correct_answer || 'Not available';
  };

  // Extract student's answer text
  const getStudentAnswerText = (answer) => {
    const { question_type, student_answer, options, true_false_answer } = answer;

    if (question_type === 'true_false') {
      // If student has points, they answered correctly but we don't have the actual selection
      if (isAnswerCorrect(answer)) {
        return 'Correctly answered (exact selection not recorded)';
      }
      return 'Incorrect or not recorded';
    }

    if (question_type === 'fill_blank') {
      return student_answer || 'Not answered';
    }

    if (question_type === 'multiple_choice' || question_type === 'single_choice') {
      if (student_answer) {
        if (Array.isArray(student_answer)) {
          const texts = student_answer.map(id => options?.find(opt => opt.id === id)?.text).filter(t => t);
          return texts.join(', ') || 'No option selected';
        }
        const opt = options?.find(opt => opt.id === student_answer);
        return opt?.text || student_answer;
      }
      const selectedOpts = options?.filter(opt => opt.is_selected === true);
      return selectedOpts?.map(opt => opt.text).join(', ') || 'Not answered';
    }

    if (question_type === 'essay') {
      return student_answer || 'Not answered';
    }

    return student_answer || 'Not answered';
  };

  // Render options for multiple/single choice
  const renderOptions = (options) => {
    if (!options || options.length === 0) return null;
    return (
      <div className="mt-4 space-y-2">
        <p className="text-sm font-medium text-gray-700 mb-2">Options:</p>
        {options.map((option, idx) => {
          let optionClasses = "p-3 rounded-lg border ";
          let indicator = null;

          if (option.is_correct === true && option.is_selected === true) {
            optionClasses += "bg-green-100 border-green-400";
            indicator = <span className="ml-2 text-green-600 text-xs font-semibold">✓ Correct (selected)</span>;
          } else if (option.is_correct === true) {
            optionClasses += "bg-green-50 border-green-300";
            indicator = <span className="ml-2 text-green-600 text-xs font-semibold">✓ Correct</span>;
          } else if (option.is_selected === true) {
            optionClasses += "bg-blue-100 border-blue-400";
            indicator = <span className="ml-2 text-blue-600 text-xs font-semibold">Your answer</span>;
          } else {
            optionClasses += "bg-gray-50 border-gray-200";
          }

          return (
            <div key={option.id || idx} className={optionClasses}>
              <div className="flex items-start justify-between">
                <div className="flex-1 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: option.text || option.label || '' }} />
                {indicator}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
        <Sidebar isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
        <div className="flex-1 pt-16 lg:pt-5 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading detailed results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!details) return null;

  const percentage = toNumber(details.percentage);
  const totalScore = toNumber(details.total_score);
  const maxScore = toNumber(details.max_score);
  const correctCount = toNumber(details.correct_answers);
  const wrongCount = toNumber(details.wrong_answers);
  const unanswered = toNumber(details.unanswered);
  const totalQuestions = correctCount + wrongCount + unanswered;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
      
      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back Button */}
          <button onClick={() => navigate('/participate/dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>

          {/* Header Card */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-6">
              <h1 className="text-2xl font-bold text-white">{details.cbt_name}</h1>
              <p className="text-blue-100 mt-1">Completed on {formatDate(details.submitted_at)}</p>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-500">Your Score</p>
                <p className={`text-2xl font-bold ${getScoreColor(percentage)}`}>{percentage.toFixed(1)}%</p>
                <p className="text-sm text-gray-500">{totalScore} / {maxScore} points</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Time Taken</p>
                <p className="text-2xl font-bold text-gray-800">{formatTime(details.time_taken)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Questions</p>
                <p className="text-2xl font-bold text-gray-800">{totalQuestions}</p>
                {correctCount > 0 && <p className="text-sm text-green-600">{correctCount} correct</p>}
                {wrongCount > 0 && <p className="text-sm text-red-600">{wrongCount} incorrect</p>}
                {unanswered > 0 && <p className="text-sm text-orange-600">{unanswered} unanswered</p>}
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${details.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {details.passed ? 'Passed' : 'Failed'}
                </span>
              </div>
            </div>
          </div>

          {/* Answer Review Section */}
          {details.answers_review && details.answers_review.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Question Review
                </h2>
              </div>
              <div className="divide-y divide-gray-100">
                {details.answers_review.map((answer, idx) => {
                  const isCorrect = isAnswerCorrect(answer); // use score-based correctness
                  const scoreObtained = toNumber(answer.score_obtained);
                  const maxQuestionScore = toNumber(answer.max_score);
                  const questionType = answer.question_type;
                  const typeInfo = getQuestionTypeInfo(questionType);
                  const hasOptions = questionType === 'multiple_choice' || questionType === 'single_choice';
                  
                  return (
                    <div key={answer.question_id || idx} className="p-6 hover:bg-gray-50 transition">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {isCorrect ? '✓' : '✗'}
                          </span>
                          <h3 className="font-semibold text-gray-900">Question {idx + 1}</h3>
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full flex items-center gap-1">
                            <span>{typeInfo.icon}</span> {typeInfo.label}
                          </span>
                        </div>
                        <button onClick={() => toggleQuestionExpand(idx)} className="text-gray-400 hover:text-gray-600">
                          <svg className={`w-5 h-5 transition-transform ${expandedQuestion === idx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      
                      <div className="ml-11">
                        {/* Question Text */}
                        <div className="prose prose-sm max-w-none text-gray-700 mb-3" dangerouslySetInnerHTML={{ __html: answer.question_text }} />
                        
                        {/* Options for multiple/single choice */}
                        {hasOptions && answer.options && renderOptions(answer.options)}
                        
                        {/* For fill_blank, true_false, essay */}
                        {(!hasOptions || questionType === 'fill_blank' || questionType === 'true_false' || questionType === 'essay') && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mt-4">
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <p className="font-medium text-blue-800 mb-1">Your Answer:</p>
                              <div className="text-gray-700">
                                {questionType === 'essay' 
                                  ? renderHtml(getStudentAnswerText(answer), 'Not answered')
                                  : <span className="whitespace-pre-wrap">{getStudentAnswerText(answer)}</span>
                                }
                              </div>
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg">
                              <p className="font-medium text-green-800 mb-1">Correct Answer:</p>
                              <div className="text-gray-700">
                                {questionType === 'essay' 
                                  ? <span className="italic text-gray-500">Essay answers are manually graded</span>
                                  : <span className="whitespace-pre-wrap">{getCorrectAnswerText(answer)}</span>
                                }
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Explanation (collapsible) */}
                        {answer.explanation && expandedQuestion === idx && (
                          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                            <p className="font-medium text-yellow-800 mb-1">Explanation:</p>
                            <p className="text-gray-700 text-sm">{answer.explanation}</p>
                          </div>
                        )}
                        
                        {/* Score and Time */}
                        <div className="mt-3 flex justify-between text-xs text-gray-400">
                          <span>Score: {scoreObtained} / {maxQuestionScore}</span>
                          <span>Time: {formatTime(answer.time_taken)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section Performance */}
          {details.section_performance && details.section_performance.length > 0 && (
            <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Section Performance</h2>
              <div className="space-y-3">
                {details.section_performance.map((section, idx) => {
                  const sectionPercent = toNumber(section.percentage);
                  return (
                    <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-800">{section.section_name}</p>
                        <p className="text-sm text-gray-500">{section.attempted || 0} / {section.total_questions} attempted</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-600">{sectionPercent.toFixed(1)}%</p>
                        <p className="text-xs text-gray-500">{toNumber(section.total_score)} / {toNumber(section.max_score)} pts</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <button onClick={() => navigate('/participate/dashboard')} className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition">
              Back to Dashboard
            </button>
            <button onClick={() => window.print()} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestResultDetailsPage;